import fs from "fs";
import path from "path";
import chalk from "chalk";
import { rm } from 'fs/promises';
import { CONFIGS } from "./configs";
import { prompt } from "./utils/prompt";
import { createApi } from "./utils/api";
import { Spinner } from "./utils/spinner";
import { ReleaseOptions } from "./utils/types";
import { getKey, setKey, deleteKey } from "./utils/keys";
import { compressBundle } from "./utils/compress";
import { executeInteractive } from "./utils/exec";
import { InteractiveHistory } from "./utils/interactive-history";
import { isValidPlatform, isValidVersion, isValidPatch } from "./utils/checks";

const api = createApi({
  baseUrl: `${CONFIGS.API_BASE_URL}${CONFIGS.API_VERSION}`,
  timeout: 5000,
  retries: 2,
});

const secureHeaders = {
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getKey("auth") ? getKey("auth").access_token : ""}`,
  }
}

export const login = async () => {

  const email = await prompt(chalk.blue("📱 Enter your email: "));
  const password = await prompt(chalk.blue("📱 Enter your password: "));
  const spinner = new Spinner(`Logging in as ${email}...`);
  try {
    const response = await api!.post<{ access_token?: string; refresh_token?: string; expires_at?: string }>(
      "/login",
      { email, password },
    );

    if (response.success && response.data) {
      const authTokens = {
        email,
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        expires_at: response.data.expires_at || new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(), // Default to 1 week if not provided
      };
      setKey("auth", authTokens);
      spinner.stop();
      console.log(chalk.green(`\n✅ Logged in successfully as ${email}\n`));
    } else {
      spinner.stop();
      throw new Error(response.error || "Login failed");
    }
  } catch (error: any) {
    spinner.stop();
    console.error(chalk.red("\n❌ Login failed:\n"), error.message || error, "\n");
    process.exit(1);
  }
};

export const refreshToken = async () => {
  const auth = getKey("auth");
  if (auth && new Date(auth.expires_at) < new Date()) {
    const spinner = new Spinner(`Auth token has expired. Re-authenticating...`);
    try {
      spinner.start();
      const response = await api!.post<{ access_token?: string; refresh_token?: string; expires_at?: string }>(
        "/refresh-token",
        { email: auth.email, refresh_token: auth.refresh_token },
      );

      if (response.success && response.data) {
        const authTokens = {
          email: auth.email,
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          expires_at: response.data.expires_at || new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(), // Default to 1 week if not provided
        };
        setKey("auth", authTokens);
        spinner.stop();
        console.log(chalk.green("✅ Auth token refreshed successfully!"));
      } else {
        spinner.stop();
        console.error(chalk.red(`❌ Failed to refresh auth token. Please login using '${chalk.italic("superpush login")}'.`));
        process.exit(1);
      }
    } catch (error: any) {
      spinner.stop();
      console.error(chalk.red("\n❌ Failed to refresh auth token:\n"), error.message || error, "\n");
      process.exit(1);
    }
  } else if (!auth) {
    console.error(chalk.red(`\n❌ No SuperPush session found. Please login first using '${chalk.italic("superpush login")}'.\n`));
    process.exit(1);
  }
};

export const upgrade = (version: string | null) => {
  if (!version) {
    version = "latest";
  }

  executeInteractive(`npm i -g superpush@${version}`)
    .then((output) => {
      console.log(chalk.green(`\n✅ Upgraded superpush version to ${version}\n`));
    })
    .catch((error) => {
      console.error(
        chalk.red(`\n\n❌ Failed to upgrade superpush version to ${version}:\n`),
        error, "\n"
      );
    });
};

export const init = async () => {
  // Ensure .superpush directory exists
  if (!fs.existsSync(CONFIGS.CONFIG_DIR_PATH)) {
    fs.mkdirSync(CONFIGS.CONFIG_DIR_PATH);
  }

  // Check if config already exists
  if (getKey(`${CONFIGS.PROJECT_DIR}`)) {
    const overwrite = await prompt(chalk.yellow("\n⚠️  Configuration file already exists. Overwrite? (y/N): "));
    if (!["y", "yes"].includes(overwrite.toLowerCase())) {
      console.log(chalk.red("❌ Initialization cancelled.\n"));
      return;
    }
  }

  console.log(chalk.blue("\n\n\t\t🚀 Welcome to SuperPush CLI! Let's set up your app...\n"));

  while (true) {
    try {
      const appName = await prompt(chalk.blue("📱 Enter your app name: "));

      // Check if user wants to quit (empty input)
      if (!appName || appName.trim() === '') {
        console.log(chalk.red("❌ App name cannot be empty. Please try again or press Ctrl+C to quit."));
        continue;
      }

      const trimmedAppName = appName.trim();
      console.log(chalk.cyan(`🔍 Checking availability for "${trimmedAppName}"...`));

      const response = await api!.post<{ id?: string; success?: boolean }>(
        "/init",
        { app_name: trimmedAppName },
        secureHeaders,
      );

      if (response.success && response.data) {
        const config = {
          app_name: trimmedAppName,
          app_id: response.data.id,
        };
        setKey(`${CONFIGS.PROJECT_DIR}`, config);
        console.log(chalk.green(`✅ App "${trimmedAppName}" created successfully!`));
        console.log(`┌─────────────────────────────────────────────────────────────────────────────────┐`)
        console.log("│", chalk.yellow("⚠️  Run the below command to ignore the configuration file from version control."), "│");
        console.log("│", chalk.gray.italic(`\techo "\\n# superpush CLI\\n${CONFIGS.CONFIG_DIR}" >> .gitignore\t\t\t `), "│");
        console.log(`└─────────────────────────────────────────────────────────────────────────────────┘`)
        return; // Exit the loop on success
      } else {
        throw new Error(response.error || "Failed to create app");
      }

    } catch (error: any) {
      const errorMessage = error.message || error;

      // Check if app name is taken
      if (errorMessage.includes("already taken") || errorMessage.includes("exists") || errorMessage.includes("unavailable")) {
        console.log(chalk.red("❌ App name is already taken. Please try a different name."));
        continue; // Continue the loop to ask for another name
      } else {
        console.error(chalk.red(`❌ Failed to create app: ${errorMessage}`));
        console.log(chalk.yellow("Please try again or press Ctrl+C to quit."));
        continue; // Continue the loop to retry
      }
    }
  }
};

const buildBundle = (platform: string) => {
  const bundleOutput = platform === CONFIGS.ANDROID ?
    CONFIGS.ANDROID_BUNDLE_OUTPUT : CONFIGS.IOS_BUNDLE_OUTPUT;

  const assetsDest = platform === CONFIGS.ANDROID ?
    CONFIGS.ANDROID_ASSET_DESTINATION : CONFIGS.IOS_ASSET_DESTINATION;

  executeInteractive(
    `npx react-native bundle \
      --platform ${platform} --dev false --entry-file index.js \
      --bundle-output ${bundleOutput} --assets-dest ${assetsDest}`
  ).then((output) => {
    console.log(chalk.green(`✅ Build successful for ${platform}\n`));
  }).catch((error) => {
    console.error(chalk.red("❌ Build failed:\n"), error, "\n");
  });
}

export const build = (platform: string | undefined) => {
  if (platform) {
    isValidPlatform(platform);
    console.log(chalk.blue(`\nBuilding for ${platform}...`));
    buildBundle(platform);
  } else {
    console.log(chalk.blue(`\nBuilding for both ${CONFIGS.ANDROID} and ${CONFIGS.IOS}...`));
    buildBundle(CONFIGS.ANDROID);
    buildBundle(CONFIGS.IOS);
  }
};

export const release = async (platform: string, release_version: string, options: ReleaseOptions) => {
  isValidPlatform(platform);
  isValidVersion(release_version);

  const superpush_config = getKey(`${CONFIGS.PROJECT_DIR}`)

  const bundleOutput = platform === CONFIGS.ANDROID ? CONFIGS.ANDROID_BUNDLE_OUTPUT : CONFIGS.IOS_BUNDLE_OUTPUT;
  const platformDir = `${CONFIGS.CONFIG_DIR}/${platform}`;
  const bundleOutputCompressed = `${CONFIGS.CONFIG_DIR}/${platform}_bundle.tar.br`;

  if (!fs.existsSync(bundleOutput)) {
    console.error(chalk.red(`\n❌ Bundle file not found. Please run 'superpush build <platform>'\n`));
    process.exit(1);
  }

  console.log("");
  const compressSpinner = new Spinner(`Compressing ${platform} bundle...`);
  try {
    compressSpinner.start();
    await compressBundle(platformDir, bundleOutputCompressed);
    compressSpinner.stop();
    console.log(chalk.green(`✅ Bundle compressed successfully\n`));
  } catch (error: any) {
    compressSpinner.stop();
    console.error(chalk.red("\n❌ Failed to compress bundle:\n"), error.message || error, "\n");
    process.exit(1);
  }

  if (!fs.existsSync(bundleOutputCompressed)) {
    console.error(chalk.red(`❌ Compressed bundle file not found. Please check the build process\n`));
    process.exit(1);
  }

  const bundleFile = fs.readFileSync(bundleOutputCompressed);

  const formData = new FormData();
  formData.append("app_id", superpush_config.app_id);
  formData.append("platform", platform);
  formData.append("release_version", release_version);
  const fileName = path.basename(bundleOutputCompressed);
  formData.append("bundle", new Blob([bundleFile]), fileName);

  if (options.message) formData.append("release_notes", options.message);
  if (options.force) formData.append("mandatory", options.force);
  if (options.rollout) formData.append("rollout", options.rollout);

  let releaseSpinner = new Spinner(`Releasing ${platform} bundle...`);
  try {
    releaseSpinner.start();
    const response = await api!.post<{ id?: string; success?: boolean }>(
      "/release",
      formData,
      { ...secureHeaders, timeout: 0, retries: 0 },
    );
    releaseSpinner.stop();
    if (response.success && response.data) {
      console.log(chalk.green(`✅ Successfully released the ${platform} bundle\n`));
    } else {
      throw new Error(response.error || "Failed to release bundle");
    }
  } catch (error: any) {
    releaseSpinner.stop();
    const errorMessage = error.message || error;
    console.error(chalk.red("\n❌ Failed to release the bundle:\n"), errorMessage, "\n");
    process.exit(1);
  }

  if (options.delete) {
    await rm(platformDir, { recursive: true, force: true });
    await rm(bundleOutputCompressed, { recursive: true, force: true });
    console.log(chalk.yellow(`🗑️  Deleted ${platform} build files\n`));
  }
};

export const rollback = async (platform: string, version: string, patch: string) => {
  isValidPlatform(platform);
  isValidVersion(version);
  patch = `${version}_${patch}`;
  isValidPatch(patch);

  const superpush_config = getKey(`${CONFIGS.PROJECT_DIR}`);

  try {
    const response = await api!.post<any>(
      "/rollback",
      { app_id: superpush_config.app_id, platform, release_version: version, composite_version: patch },
      secureHeaders,
    );
    if (response.success && response.data) {
      console.log(chalk.green(`✅ ${response.data.message}`));
    } else {
      throw new Error(response.error || "Failed to fetch release history");
    }
  } catch (error: any) {
    const errorMessage = error.message || error;
    console.error(chalk.red("\n❌ Failed to rollback:\n"), errorMessage, "\n");
  }
}

export const history = async (platform: string) => {
  isValidPlatform(platform);

  const superpush_config = getKey(`${CONFIGS.PROJECT_DIR}`);

  try {
    const response = await api!.get<any>(
      "/releases", { app_id: superpush_config.app_id, platform }, secureHeaders,
    );

    if (response.success && response.data) {
      const interactiveHistory = new InteractiveHistory(response.data);
      await interactiveHistory.start(superpush_config.app_id, platform);
    } else {
      throw new Error(response.error || "Failed to fetch release history");
    }
  } catch (error: any) {
    const errorMessage = error.message || error;
    console.error(chalk.red("\n❌ Failed to fetch release history:\n"), errorMessage, "\n");
  }
}

export const disable = async (platform: string, version: string, patch: string) => {
  isValidPlatform(platform);
  isValidVersion(version);
  patch = `${version}_${patch}`;
  isValidPatch(patch);

  const superpush_config = getKey(`${CONFIGS.PROJECT_DIR}`);

  try {
    const response = await api!.post<any>(
      "/disable",
      { app_id: superpush_config.app_id, platform, release_version: version, composite_version: patch },
      secureHeaders,
    );
    if (response.success && response.data) {
      console.log(chalk.green(`✅ ${response.data.message}`));
    } else {
      throw new Error(response.error || "Failed to disable the patch");
    }
  } catch (error: any) {
    const errorMessage = error.message || error;
    console.error(chalk.red("\n❌ Failed to disable:\n"), errorMessage, "\n");
  }
}

export const logout = async () => {
  const auth = getKey("auth");
  if (!auth) {
    console.error(chalk.red(`\n❌ No SuperPush session found. Please login first using '${chalk.italic("superpush login")}'.\n`));
    return;
  }

  const confirm = await prompt(chalk.yellow(`\nAre you sure you want to logout from SuperPush? (y/N): `));
  if (!["y", "yes"].includes(confirm.toLowerCase())) {
    console.log(chalk.red("❌ Logout cancelled.\n"));
    return;
  }

  deleteKey("auth");
  console.log(chalk.green(`✅ Logged out successfully from SuperPush as ${auth.email}\n`));
};
